import type {
  Coding,
  Observation,
  Patient,
  Reference,
} from '@medplum/fhirtypes'
import { ExtendedObservation } from '@seen/core/models/observation'
import type { DerivationRule } from '@seen/core/observation-logic/derivations/rules'
import type { ConditionEnv } from '@seen/core/observation-logic/evaluate'
import {
  panelDerivedItems,
  panelDerivedRule,
  panelOverrides,
  withDerivedAnswers,
} from '@seen/core/observation-logic/instruments/derivedItems'
import {
  buildPanelObservation,
  fromInstrumentObservations,
  type InstrumentAnswers,
  instrumentAnswerKey,
  instrumentGroupQuery,
  instrumentMemberQuery,
  instrumentPanelSummary,
  observationGroupId,
  recomputeTotal,
  upsertItemObservation,
} from '@seen/core/observation-logic/instruments/records'
import { instrumentHiddenItems } from '@seen/core/observation-logic/noteCapture'
import type { ObservationValue } from '@seen/core/observation-logic/value'
import { instrumentItems } from '@seen/core/terminology/instruments/registry'
import type {
  InstrumentItem,
  InstrumentTemplate,
} from '@seen/core/terminology/instruments/types'
import type { ObservationPanelSummary } from '@seen/core/tiptap/schema'
import { useQueryClient } from '@tanstack/react-query'
import { isEqual } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { instrumentEnv } from '#src/components/rich-text-editor/ObservationNode/panel/instrumentItems.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface InstrumentPanelAttributes {
  id: string | null
  templateId: string | null
  version: string | null
  groupId: string | null
  panelReference: string | null
  mode: string | null
  frozenReference: string | null
  summary: ObservationPanelSummary | null
  // The answers held in the note, by item, until it is signed.
  answers?: InstrumentAnswers | null
  // A template's mark that every item it shows needs an answer.
  required?: boolean
  // Calculated items set by hand, by answer key: not recalculated.
  derivedOverrides?: string[] | null
}

export interface PriorAnswer {
  value: ObservationValue
  // The record's day (YYYY-MM-DD).
  date?: string
  // The capture it was part of.
  groupId?: string
}

export interface InstrumentCapture {
  answers: InstrumentAnswers
  // The participant's latest earlier answer to each item, by answer key:
  // offered on its row, or carried over when from this visit.
  priors: Readonly<Record<string, PriorAnswer>>
  // Whether this capture's own records and the earlier ones are read.
  ready: boolean
  summary: ObservationPanelSummary
  totalReference: string | null
  answer: (item: InstrumentItem, value: ObservationValue | null) => void
  // Items an instrument condition hides now, by answer key.
  hiddenKeys: ReadonlySet<string>
  // Items a calculation fills, by answer key, with its rule.
  derived: ReadonlyMap<string, DerivationRule>
  // Calculated items the clinician set by hand, by answer key.
  overrides: ReadonlySet<string>
  // Drops a hand-set value on a calculated item and recalculates it.
  recalculate: (item: InstrumentItem) => void
}

// How long answers given in quick succession wait before the total and the
// panel are written, so they are written once.
export const TOTAL_DEBOUNCE_MS = 400

// An instrument captured in a note: the answers recorded so far (read back
// from the capture's Observations, found by its group id), and answering an
// item, which records it (created on the first answer, updated after) and
// then, once answers settle, the total and the panel. Deferred, the answers
// are the node's `answers` and no record is written: the note's signing
// writes them from there.
export function useInstrumentCapture({
  template,
  attributes,
  updateAttributes,
  participant,
  recordStatus,
  variable,
  effectiveDateTime,
  deferred = false,
}: {
  template: InstrumentTemplate
  attributes: InstrumentPanelAttributes
  updateAttributes: (attributes: Partial<InstrumentPanelAttributes>) => void
  participant: Reference<Patient> | undefined
  recordStatus: 'preliminary' | 'final'
  // Reads the context variables item conditions may use (age, sex).
  variable: ConditionEnv['variable']
  // When new records were taken: the note's encounter start, else now.
  effectiveDateTime?: string
  deferred?: boolean
}): InstrumentCapture {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const practitioner = useCurrentPractitioner()
  const groupId = attributes.groupId
  const items = useMemo(() => instrumentItems(template), [template])
  const derived = useMemo(
    () =>
      new Map(
        panelDerivedItems(template).map(({ item, rule }) => [
          instrumentAnswerKey(item.coding),
          rule,
        ])
      ),
    [template]
  )
  const overrideList = useMemo(() => panelOverrides(attributes), [attributes])

  /* What the note holds, when deferred. Kept on the node once the note is
   * signed or submitted, so a read of the note shows its answers from the
   * note itself rather than from records that may not be written yet. */
  const held = useMemo<InstrumentAnswers>(
    () => attributes.answers ?? {},
    [attributes.answers]
  )
  const fromNote = deferred || attributes.answers != null

  const { data: members } = useSearch(
    ExtendedObservation,
    !fromNote && groupId && participant
      ? {
          ...instrumentGroupQuery({
            subject: participant,
            groupIds: [groupId],
          }),
          _count: 100,
        }
      : undefined,
    { silentErrors: true }
  )
  const recorded = useMemo(
    () =>
      fromInstrumentObservations(
        template,
        (members ?? []).map((member) => member.toFhirResource())
      ).answers,
    [members, template]
  )
  // Answers given here, ahead of their records coming back.
  const [given, setGiven] = useState<Record<string, ObservationValue | null>>(
    {}
  )
  // Another participant's answers given here do not carry over.
  const participantReference = participant?.reference
  useEffect(() => {
    setGiven((previous) => (Object.keys(previous).length ? {} : previous))
  }, [participantReference])
  const answers = useMemo<InstrumentAnswers>(
    () => (fromNote ? held : { ...recorded, ...given }),
    [fromNote, held, recorded, given]
  )

  const { data: earlier } = useSearch(
    ExtendedObservation,
    participant
      ? {
          patient: participant.reference,
          code: items.map((item) => item.coding),
          'status:not': 'entered-in-error',
          _sort: '-date',
          _count: 100,
        }
      : undefined,
    { silentErrors: true }
  )
  const priors = useMemo(() => {
    const prior: Record<string, PriorAnswer> = {}
    const fromOthers = (earlier ?? [])
      .map((observation) => observation.toFhirResource())
      .filter((observation) => observationGroupId(observation) !== groupId)
    for (const observation of fromOthers) {
      const read = fromInstrumentObservations(template, [observation]).answers
      for (const [key, value] of Object.entries(read)) {
        // Only an exact system|code match answers this item.
        const exact = observation.code.coding?.some(
          (coding) => instrumentAnswerKey(coding) === key
        )
        if (!(key in prior) && value && exact) {
          prior[key] = {
            value,
            date: observation.effectiveDateTime?.slice(0, 10),
            groupId: observationGroupId(observation),
          }
        }
      }
    }
    return prior
  }, [earlier, groupId, template])

  // Items whose own condition hides them, for the total to leave out.
  const hiddenFor = useCallback(
    (current: InstrumentAnswers): Coding[] =>
      instrumentHiddenItems(template, current, variable),
    [template, variable]
  )
  // What the score's rule and bands read (sex, age), with the answers.
  const optionsFor = useCallback(
    (current: InstrumentAnswers) => ({
      hidden: hiddenFor(current),
      env: instrumentEnv(template, current, variable),
    }),
    [template, variable, hiddenFor]
  )
  const summary = useMemo(
    () => instrumentPanelSummary(template, answers, optionsFor(answers)),
    [template, answers, optionsFor]
  )
  // Rendered items: those no condition hides.
  const hiddenKeys = useMemo(
    () => new Set(hiddenFor(answers).map(instrumentAnswerKey)),
    [answers, hiddenFor]
  )
  // A held answer to an item that is now hidden stays held (it returns if
  // the item is shown again) but is not offered, and the signing leaves it
  // unwritten.
  const shownAnswers = useMemo<InstrumentAnswers>(
    () =>
      fromNote
        ? Object.fromEntries(
            Object.entries(answers).filter(([key]) => !hiddenKeys.has(key))
          )
        : answers,
    [fromNote, answers, hiddenKeys]
  )

  const totalReference = useMemo(() => {
    const total = template.total
    if (!total) {
      return null
    }
    return (
      members?.find((member) =>
        member.codeableConcept.coding?.some(
          (coding) =>
            coding.code === total.coding.code &&
            coding.system === total.coding.system
        )
      )?.reference.reference ?? null
    )
  }, [members, template])

  // The latest state for the debounced write, read when it runs.
  const latest = useRef({
    answers,
    groupId,
    members,
    participant: participantReference,
    overrides: overrideList,
  })
  latest.current = {
    answers,
    groupId,
    members,
    participant: participantReference,
    overrides: overrideList,
  }
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  // The total write waiting on the timer. It runs early rather than being
  // dropped when the next one is for another participant or the panel
  // moves on to one.
  const pendingTotal = useRef<{ participant?: string; run: () => void }>(
    undefined
  )
  // Each item's write, by participant, group and answer key: a later answer
  // waits for the one before and updates the record it returned, so a
  // correction made before the members are read back does not create a
  // second record.
  const itemWrites = useRef(new Map<string, Promise<Observation | undefined>>())

  const writeTotalAndPanel = useCallback(
    async (group: string, scheduled: InstrumentAnswers): Promise<void> => {
      if (!participant) {
        return
      }
      const context = {
        template,
        subject: participant,
        performer: practitioner.reference,
        status: recordStatus,
        groupId: group,
        effectiveDateTime,
      }
      const current = await medplum.searchResources(
        'Observation',
        {
          ...instrumentGroupQuery({ subject: participant, groupIds: [group] }),
          _count: '100',
        },
        { cache: 'no-cache' }
      )
      const byCode = (code: string | undefined): Observation | undefined =>
        current.find((member) =>
          member.code.coding?.some((coding) => coding.code === code)
        )
      // The live answers, unless the panel has moved on to another
      // participant: then the ones this write was scheduled with.
      const answersNow =
        latest.current.participant === participant.reference
          ? latest.current.answers
          : scheduled
      const options = optionsFor(answersNow)
      const hiddenNow = new Set((options.hidden ?? []).map(instrumentAnswerKey))
      // A record answered before its item was hidden no longer stands.
      await Promise.all(
        items
          .filter((item) => hiddenNow.has(instrumentAnswerKey(item.coding)))
          .flatMap((item) => {
            const member = byCode(item.coding.code)
            return member && member.status !== 'entered-in-error'
              ? [
                  medplum.updateResource({
                    ...member,
                    status: 'entered-in-error',
                  }),
                ]
              : []
          })
      )
      const itemReferences = Object.fromEntries(
        items.flatMap((item) => {
          if (hiddenNow.has(instrumentAnswerKey(item.coding))) {
            return []
          }
          const member = byCode(item.coding.code)
          return member?.id
            ? [
                [
                  instrumentAnswerKey(item.coding),
                  { reference: `Observation/${member.id}` },
                ],
              ]
            : []
        })
      )
      const totalExisting = template.total
        ? byCode(template.total.coding.code)
        : undefined
      const total = recomputeTotal({
        ...context,
        answers: answersNow,
        itemReferences,
        existing: totalExisting,
        options,
      })
      let totalSaved: Observation | undefined
      if (total) {
        totalSaved = total.id
          ? await medplum.updateResource(total)
          : await medplum.createResourceIfNoneExist(
              total,
              instrumentMemberQuery({
                subject: participant,
                groupId: group,
                role: 'score',
                coding: template.total?.coding,
              })
            )
      }
      // Each subtotal is its own score record, as a complete capture writes
      // them (`buildInstrumentObservations`).
      const subtotalsSaved = await Promise.all(
        (template.subtotals ?? []).map(async (score) => {
          const subtotal = recomputeTotal({
            ...context,
            answers: answersNow,
            itemReferences,
            existing: byCode(score.coding.code),
            score,
            options,
          })
          if (!subtotal) {
            return undefined
          }
          return subtotal.id
            ? medplum.updateResource(subtotal)
            : medplum.createResourceIfNoneExist(
                subtotal,
                instrumentMemberQuery({
                  subject: participant,
                  groupId: group,
                  role: 'score',
                  coding: score.coding,
                })
              )
        })
      )
      const panelExisting = current.find(
        (member) =>
          member.code.coding?.some(
            (coding) => coding.code === template.panel.code
          ) ?? false
      )
      const memberReferences = [
        ...Object.values(itemReferences),
        ...[totalSaved, ...subtotalsSaved].flatMap((saved) =>
          saved?.id ? [{ reference: `Observation/${saved.id}` }] : []
        ),
      ]
      const panel = buildPanelObservation({
        ...context,
        members: memberReferences,
        existing: panelExisting,
      })
      const panelSaved = panel.id
        ? await medplum.updateResource(panel)
        : await medplum.createResourceIfNoneExist(
            panel,
            instrumentMemberQuery({
              subject: participant,
              groupId: group,
              role: 'panel',
            })
          )
      // The node's panel and summary follow the participant it shows; a
      // write finishing after the panel moved to another leaves them.
      if (latest.current.participant === participant.reference) {
        updateAttributes({
          panelReference: panelSaved.id ? `Observation/${panelSaved.id}` : null,
          summary: instrumentPanelSummary(template, answersNow, options),
        })
      }
      // Every Observation read and search, the note's condition values
      // among them, and nothing else on the page.
      cacheKeys.invalidateResourceType(queryClient, 'Observation')
    },
    [
      participant,
      template,
      practitioner.reference,
      recordStatus,
      medplum,
      items,
      updateAttributes,
      queryClient,
      optionsFor,
      effectiveDateTime,
    ]
  )

  const scheduleTotal = useCallback(
    (group: string, scheduled: InstrumentAnswers): void => {
      if (!participant) {
        return
      }
      clearTimeout(timer.current)
      const pending = pendingTotal.current
      if (pending && pending.participant !== participant.reference) {
        pending.run()
      }
      const run = (): void => {
        pendingTotal.current = undefined
        void writeTotalAndPanel(group, scheduled)
      }
      pendingTotal.current = { participant: participant.reference, run }
      timer.current = setTimeout(run, TOTAL_DEBOUNCE_MS)
    },
    [participant, writeTotalAndPanel]
  )
  useEffect(
    () => () => {
      const pending = pendingTotal.current
      if (pending) {
        clearTimeout(timer.current)
        pending.run()
      }
    },
    [participantReference]
  )

  // Writes one item's record: created on its first answer, updated after,
  // each write waiting for the one before.
  const writeItem = useCallback(
    (
      group: string,
      item: InstrumentItem,
      value: ObservationValue | null,
      calculated: DerivationRule | undefined
    ): Promise<Observation | undefined> => {
      if (!participant) {
        return Promise.resolve(undefined)
      }
      const key = instrumentAnswerKey(item.coding)
      const writeKey = `${participant.reference}|${group}|${key}`
      const previous =
        itemWrites.current.get(writeKey) ?? Promise.resolve(undefined)
      const write = previous
        .catch(() => undefined)
        .then(async (written) => {
          const member = latest.current.members
            ?.find((candidate) =>
              candidate.codeableConcept.coding?.some(
                (coding) => coding.code === item.coding.code
              )
            )
            ?.toFhirResource()
          const record = upsertItemObservation({
            template,
            subject: participant,
            performer: practitioner.reference,
            status: recordStatus,
            groupId: group,
            effectiveDateTime,
            item,
            value,
            calculated,
            existing: newerRecord(
              ownedBy(written, participant),
              ownedBy(member, participant)
            ),
          })
          return record.id
            ? medplum.updateResource(record)
            : medplum.createResourceIfNoneExist(
                record,
                instrumentMemberQuery({
                  subject: participant,
                  groupId: group,
                  role: 'item',
                  coding: item.coding,
                })
              )
        })
      itemWrites.current.set(writeKey, write)
      return write
    },
    [
      participant,
      template,
      practitioner.reference,
      recordStatus,
      medplum,
      effectiveDateTime,
    ]
  )

  const answer = useCallback(
    (item: InstrumentItem, value: ObservationValue | null): void => {
      if (!deferred && !participant) {
        return
      }
      const group = latest.current.groupId ?? crypto.randomUUID()
      const key = instrumentAnswerKey(item.coding)
      const previousAnswers = latest.current.answers
      const answered = {
        ...previousAnswers,
        [key]: value,
      }
      const hiddenNow = hiddenFor(answered).map(instrumentAnswerKey)
      if (hiddenNow.includes(key)) {
        return
      }
      // An answer that hides another item drops that item's answer.
      const dropped = hiddenNow.filter((hidden) => answered[hidden])
      for (const hidden of dropped) {
        answered[hidden] = null
      }
      // A value typed on a calculated item overrides it; clearing it hands
      // the item back to its calculation.
      const previousOverrides = latest.current.overrides
      const overrides = derived.has(key)
        ? value === null
          ? previousOverrides.filter((override) => override !== key)
          : [...new Set([...previousOverrides, key])]
        : previousOverrides
      const overridesChanged = overrides !== previousOverrides
      const nextAnswers = withDerivedAnswers(template, answered, {
        overrides,
        variable,
      })
      // Calculated items whose value moved with this answer.
      const recalculated = items.filter((candidate) => {
        const candidateKey = instrumentAnswerKey(candidate.coding)
        return (
          candidateKey !== key &&
          derived.has(candidateKey) &&
          !isEqual(
            nextAnswers[candidateKey] ?? null,
            previousAnswers[candidateKey] ?? null
          )
        )
      })
      latest.current = {
        ...latest.current,
        answers: nextAnswers,
        groupId: group,
        overrides,
      }
      const answerAttributes: Partial<InstrumentPanelAttributes> = {}
      if (!attributes.groupId) {
        answerAttributes.groupId = group
      }
      if (overridesChanged) {
        answerAttributes.derivedOverrides = overrides
      }
      if (deferred) {
        const nextHeld: Record<string, ObservationValue> = {}
        for (const [heldKey, heldValue] of Object.entries(nextAnswers)) {
          if (heldValue) {
            nextHeld[heldKey] = heldValue
          }
        }
        latest.current.answers = nextHeld
        updateAttributes({
          ...answerAttributes,
          answers: nextHeld,
          summary: instrumentPanelSummary(
            template,
            nextHeld,
            optionsFor(nextHeld)
          ),
        })
        return
      }
      if (!participant) {
        return
      }
      setGiven((previous) => ({
        ...previous,
        ...Object.fromEntries(dropped.map((hidden) => [hidden, null])),
        ...Object.fromEntries(
          recalculated.map((candidate) => [
            instrumentAnswerKey(candidate.coding),
            nextAnswers[instrumentAnswerKey(candidate.coding)] ?? null,
          ])
        ),
        [key]: nextAnswers[key] ?? null,
      }))
      updateAttributes({
        ...answerAttributes,
        summary: instrumentPanelSummary(
          template,
          nextAnswers,
          optionsFor(nextAnswers)
        ),
      })
      const calculatedFor = (candidate: InstrumentItem) =>
        panelDerivedRule(template, candidate.coding, overrides)
      const writes = [item, ...recalculated].map((candidate) =>
        writeItem(
          group,
          candidate,
          nextAnswers[instrumentAnswerKey(candidate.coding)] ?? null,
          calculatedFor(candidate)
        )
      )
      // The answers as given here: the live ones may be another
      // participant's by the time the total is written.
      void Promise.all(writes).then(() => scheduleTotal(group, nextAnswers))
    },
    [
      deferred,
      participant,
      updateAttributes,
      attributes.groupId,
      template,
      items,
      derived,
      variable,
      scheduleTotal,
      hiddenFor,
      optionsFor,
      writeItem,
    ]
  )

  const recalculate = useCallback(
    (item: InstrumentItem): void => answer(item, null),
    [answer]
  )

  // An item hidden by something other than an answer here (the age, say)
  // can still hold a record: once seen, it is withdrawn with the next write.
  const staleHidden = !deferred && [...hiddenKeys].some((key) => recorded[key])
  useEffect(() => {
    if (!staleHidden || !groupId) {
      return
    }
    scheduleTotal(groupId, latest.current.answers)
  }, [staleHidden, groupId, scheduleTotal])

  const ready =
    (deferred || !groupId || members !== undefined) &&
    (!participant || earlier !== undefined)
  return {
    answers: shownAnswers,
    priors,
    ready,
    summary,
    totalReference,
    answer,
    hiddenKeys,
    derived,
    overrides: new Set(overrideList),
    recalculate,
  }
}

// A record only counts for the participant it is about: updating another's
// would change that chart.
function ownedBy(
  record: Observation | undefined,
  participant: Reference<Patient>
): Observation | undefined {
  return record?.subject?.reference === participant.reference
    ? record
    : undefined
}

// The later of the record this capture last wrote and the one read back.
function newerRecord(
  written: Observation | undefined,
  read: Observation | undefined
): Observation | undefined {
  if (!written || !read) {
    return written ?? read
  }
  return (read.meta?.lastUpdated ?? '') > (written.meta?.lastUpdated ?? '')
    ? read
    : written
}
